import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Wallet,
  ArrowUpCircle,
  Key,
  Shield,
  Settings,
  ArrowLeft,
  Menu,
  X
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuth } from '@/features/auth/contexts/AuthContext'

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
  { id: 'users', label: 'Usuarios', icon: Users, path: '/admin/users' },
  { id: 'deposits', label: 'Depósitos', icon: Wallet, path: '/admin/deposits' },
  { id: 'withdrawals', label: 'Retiros', icon: ArrowUpCircle, path: '/admin/withdrawals' },
  { id: 'codes', label: 'Códigos', icon: Key, path: '/admin/codes' },
  { id: 'kyc', label: 'KYC', icon: Shield, path: '/admin/kyc' },
  { id: 'config', label: 'Configuración', icon: Settings, path: '/admin/config' },
]

export default function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-casino-black flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-64 flex-col bg-casino-black-light border-r border-casino-gold/20">
        {/* Header */}
        <div className="h-16 flex items-center px-6 border-b border-casino-gold/20">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/lobby')}
            className="text-casino-gold hover:text-casino-gold-light mr-3"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-cinzel text-casino-gold font-bold">ADMIN</h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors',
                location.pathname === item.path
                  ? 'bg-casino-gold/20 text-casino-gold'
                  : 'text-gray-400 hover:bg-casino-gold/10 hover:text-white'
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </button>
          ))}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-casino-gold/20">
          <p className="text-sm text-gray-400">Logueado como</p>
          <p className="text-white font-medium truncate">{user.displayName || user.email}</p>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/80" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 h-full w-64 bg-casino-black-light border-r border-casino-gold/20 z-50">
            <div className="h-16 flex items-center justify-between px-6 border-b border-casino-gold/20">
              <h1 className="font-cinzel text-casino-gold font-bold">ADMIN</h1>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="p-4 space-y-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.path)
                    setSidebarOpen(false)
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors',
                    location.pathname === item.path
                      ? 'bg-casino-gold/20 text-casino-gold'
                      : 'text-gray-400 hover:bg-casino-gold/10 hover:text-white'
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </button>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <header className="lg:hidden h-16 bg-casino-black-light border-b border-casino-gold/20 flex items-center px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="font-cinzel text-casino-gold font-bold ml-4">ADMIN</h1>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
